import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./CervelloBetaLumina.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const examples = [
  "Cosa dice la wiki sulla UNI 11224 per il controllo periodico?",
  "Come è strutturato il sistema Urmet 2Voice nella mia wiki?",
  "Quali note ho sulle uscite della Ksenia Lares 40?",
];

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT");
};

const fmtSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtTokens = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? new Intl.NumberFormat("it-IT").format(n) : "—";
};

const fmtCredits = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  if (n < 0.01) return n.toLocaleString("it-IT", { minimumFractionDigits: 4, maximumFractionDigits: 6 });
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const fmtMoney = (value, currency = "usd") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
      minimumFractionDigits: n > 0 && n < 0.01 ? 4 : 2,
      maximumFractionDigits: n > 0 && n < 0.01 ? 6 : 2,
    }).format(n);
  } catch {
    return `${n.toFixed(4)} ${String(currency || "usd").toUpperCase()}`;
  }
};

const markdownBodyOnly = (text) => {
  const value = String(text || "").replace(/\r\n/g, "\n");
  if (!value.startsWith("---\n")) return value;
  const end = value.indexOf("\n---\n", 4);
  return end >= 0 ? value.slice(end + 5) : value;
};

const renderInlineMarkdown = (text, sources, openSource) => {
  const parts = String(text || "").split(
    /(\*\*[^*]+\*\*|`[^`]+`|\[Fonte\s+\d+\])/gi
  );

  return parts.map((part, index) => {
    const key = `md-inline-${index}`;

    const bold = part.match(/^\*\*(.+)\*\*$/s);
    if (bold) {
      return <strong key={key}>{bold[1]}</strong>;
    }

    const code = part.match(/^`(.+)`$/s);
    if (code) {
      return <code key={key} className="brain-inline-code">{code[1]}</code>;
    }

    const citation = part.match(/^\[Fonte\s+(\d+)\]$/i);
    if (citation) {
      const sourceId = Number(citation[1]);
      const source = (sources || []).find(
        (item) => Number(item.sourceId) === sourceId
      );

      if (source) {
        return (
          <button
            key={key}
            type="button"
            className="brain-inline-source"
            onClick={() => openSource(source)}
            title={source.path}
          >
            Fonte {sourceId}
          </button>
        );
      }

      return (
        <span key={key} className="brain-inline-source static">
          Fonte {sourceId}
        </span>
      );
    }

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

const MarkdownMessage = ({ text, sources, openSource }) => {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");

  return (
    <div className="brain-markdown">
      {lines.map((rawLine, index) => {
        const line = rawLine.trimEnd();
        const key = `md-line-${index}`;

        if (!line.trim()) {
          return <div key={key} className="brain-md-gap" aria-hidden="true" />;
        }

        const h3 = line.match(/^###\s+(.+)$/);
        if (h3) {
          return (
            <h4 key={key}>
              {renderInlineMarkdown(h3[1], sources, openSource)}
            </h4>
          );
        }

        const h2 = line.match(/^##\s+(.+)$/);
        if (h2) {
          return (
            <h3 key={key}>
              {renderInlineMarkdown(h2[1], sources, openSource)}
            </h3>
          );
        }

        const h1 = line.match(/^#\s+(.+)$/);
        if (h1) {
          return (
            <h2 key={key}>
              {renderInlineMarkdown(h1[1], sources, openSource)}
            </h2>
          );
        }

        const quote = line.match(/^>\s?(.*)$/);
        if (quote) {
          return (
            <blockquote key={key}>
              {renderInlineMarkdown(quote[1], sources, openSource)}
            </blockquote>
          );
        }

        const bullet = line.match(/^\s*[-*]\s+(.+)$/);
        if (bullet) {
          return (
            <div key={key} className="brain-md-bullet">
              <span aria-hidden="true">•</span>
              <div>{renderInlineMarkdown(bullet[1], sources, openSource)}</div>
            </div>
          );
        }

        const ordered = line.match(/^\s*(\d+)\.\s+(.+)$/);
        if (ordered) {
          return (
            <div key={key} className="brain-md-number">
              <span>{ordered[1]}.</span>
              <div>{renderInlineMarkdown(ordered[2], sources, openSource)}</div>
            </div>
          );
        }

        return (
          <p key={key}>
            {renderInlineMarkdown(line, sources, openSource)}
          </p>
        );
      })}
    </div>
  );
};

export default function CervelloBetaLumina() {
  const [status, setStatus] = useState(null);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [asking, setAsking] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [queue, setQueue] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [apiUsageError, setApiUsageError] = useState("");
  const [apiUsageRefreshing, setApiUsageRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [processMessage, setProcessMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [previewOriginal, setPreviewOriginal] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingCandidate, setSavingCandidate] = useState(false);
  const [committingCandidate, setCommittingCandidate] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");
  const auth = useMemo(() => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/cervello/status`, auth);
      setStatus(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Cervello non disponibile");
    }
  };

  const loadQueue = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/cervello/queue`, auth);
      setQueue(r.data);
    } catch (e) {
      setUploadMessage(e?.response?.data?.error || e.message || "Coda documenti non disponibile");
    }
  };

  const loadApiUsage = async (force = false) => {
    if (force) setApiUsageRefreshing(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/cervello/api-usage`, {
        ...auth,
        params: force ? { refresh: 1 } : undefined,
      });
      setApiUsage(r.data);
      setApiUsageError("");
    } catch (e) {
      setApiUsageError(e?.response?.data?.error || e.message || "Utilizzo API OpenAI non disponibile");
    } finally {
      if (force) setApiUsageRefreshing(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadQueue();
    loadApiUsage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasProcessingJob = Boolean(queue?.items?.some((item) => item?.job?.status === "processing"));

  useEffect(() => {
    if (!hasProcessingJob) return undefined;
    const timer = window.setInterval(() => {
      loadQueue();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasProcessingJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadDocument = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    setUploadMessage("");
    try {
      const form = new FormData();
      form.append("document", file);
      const r = await axios.post(`${API_BASE_URL}/api/cervello/upload`, form, {
        ...auth,
        headers: { ...(auth.headers || {}) },
      });
      setUploadMessage(`In coda: ${r.data?.item?.name || file.name}`);
      await loadQueue();
    } catch (e) {
      setUploadMessage(e?.response?.data?.error || e.message || "Caricamento fallito");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadDocument(file);
  };

  const processDocument = async (item, force = false) => {
    if (!item?.name) return;
    setProcessMessage("");
    try {
      const r = await axios.post(`${API_BASE_URL}/api/cervello/queue/process`, { name: item.name, force }, auth);
      const status = r.data?.job?.status;
      setProcessMessage(status === "ready"
        ? `Candidato già pronto: ${item.name}`
        : `Codex sta processando: ${item.name}`);
      await Promise.all([loadQueue(), loadApiUsage()]);
    } catch (e) {
      setProcessMessage(e?.response?.data?.error || e.message || "Elaborazione non avviata");
    }
  };

  const openCandidate = async (jobId) => {
    if (!jobId) return;
    setPreviewLoading(true);
    setProcessMessage("");
    try {
      const r = await axios.get(`${API_BASE_URL}/api/cervello/queue/preview`, { ...auth, params: { jobId } });
      setPreview(r.data);
      setPreviewText(r.data?.content || "");
      setPreviewOriginal(r.data?.content || "");
    } catch (e) {
      setProcessMessage(e?.response?.data?.error || e.message || "Anteprima non disponibile");
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveCandidate = async () => {
    if (!preview?.job?.id || savingCandidate) return null;
    setSavingCandidate(true);
    setProcessMessage("");
    try {
      const r = await axios.post(`${API_BASE_URL}/api/cervello/queue/candidate`, {
        jobId: preview.job.id,
        content: previewText,
      }, auth);
      setPreview((old) => ({ ...old, job: r.data.job, content: r.data.content }));
      setPreviewOriginal(r.data.content || previewText);
      setProcessMessage("Correzioni del candidato salvate.");
      await Promise.all([loadQueue(), loadApiUsage()]);
      return r.data;
    } catch (e) {
      setProcessMessage(e?.response?.data?.error || e.message || "Salvataggio candidato fallito");
      return null;
    } finally {
      setSavingCandidate(false);
    }
  };

  const commitCandidate = async () => {
    if (!preview?.job?.id || committingCandidate) return;
    const ok = window.confirm(
      `Inserire "${preview.job.title || preview.job.sourceName}" nel Cervello?\n\n` +
      "La nota verrà scritta nella wiki, il documento originale verrà spostato in raw/processati e l'indice verrà aggiornato."
    );
    if (!ok) return;

    setCommittingCandidate(true);
    setProcessMessage("");
    try {
      if (previewText !== previewOriginal) {
        const saved = await saveCandidate();
        if (!saved) return;
      }
      const r = await axios.post(`${API_BASE_URL}/api/cervello/queue/commit`, { jobId: preview.job.id }, auth);
      setProcessMessage(`Inserito nel Cervello: ${r.data?.wikiPath || preview.job.title}`);
      setPreview(null);
      setPreviewText("");
      setPreviewOriginal("");
      await Promise.all([loadQueue(), loadStatus(), loadApiUsage()]);
    } catch (e) {
      setProcessMessage(e?.response?.data?.error || e.message || "Inserimento nel Cervello fallito");
    } finally {
      setCommittingCandidate(false);
    }
  };

  const ask = async (preset) => {
    const q = String(preset ?? question).trim();
    if (!q || asking) return;
    const history = messages.slice(-6).filter((m) => ["user", "assistant"].includes(m.role)).map((m) => ({ role: m.role, content: m.content }));
    setMessages((old) => [...old, { id: `u-${Date.now()}`, role: "user", content: q }]);
    setQuestion("");
    setAsking(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/api/cervello/ask`, { question: q, history, limit: 8 }, auth);
      setMessages((old) => [...old, {
        id: `a-${Date.now()}`,
        role: "assistant",
        content: r.data.answer || "Dato non presente nella wiki.",
        sources: r.data.sources || [],
        model: r.data.model || null,
      }]);
      loadStatus();
    } catch (e) {
      setMessages((old) => [...old, { id: `e-${Date.now()}`, role: "error", content: e?.response?.data?.error || e.message || "Errore" }]);
    } finally {
      setAsking(false);
    }
  };

  const reindex = async () => {
    if (reindexing) return;
    setReindexing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/cervello/reindex`, { force: false }, auth);
      await loadStatus();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Reindicizzazione fallita");
    } finally {
      setReindexing(false);
    }
  };

  const openSource = async (item) => {
    setSource({ ...item, text: "" });
    setSourceLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/cervello/source`, { ...auth, params: { path: item.path } });
      setSource({ ...item, ...r.data });
    } catch (e) {
      setSource({ ...item, error: e?.response?.data?.error || e.message || "Fonte non disponibile" });
    } finally {
      setSourceLoading(false);
    }
  };

  return (
    <section className="brain-page">
      <header className="brain-head">
        <div className="brain-title">
          <div className="brain-logo">🧠</div>
          <div>
            <span>SECONDO CERVELLO TECNICO</span>
            <h2>Cervello BetaLumina</h2>
            <p>Interroga la wiki Obsidian del NAS. I documenti nuovi vengono processati solo su richiesta e scritti nella wiki esclusivamente dopo la tua conferma.</p>
          </div>
        </div>
        <div className="brain-stats">
          <div><span>Wiki</span><strong>{status?.ready ? "Connessa" : "…"}</strong><small>{status?.notes ?? "—"} note</small></div>
          <div><span>Indice</span><strong>{status?.chunks ?? "—"} blocchi</strong><small>{fmt(status?.generatedAt)}</small></div>
          <div><span>Modello</span><strong>{status?.model || "—"}</strong><small>{status?.embeddingModel || "—"}</small></div>
          <div><span>Sicurezza</span><strong>Controllata</strong><small>Scrittura solo su conferma</small></div>
        </div>
        <section className="brain-api-usage" aria-label="Utilizzo API OpenAI">
          <div className="brain-api-usage-head">
            <div>
              <span>UTILIZZO API OPENAI</span>
              <strong>Spesa reale e consumo API dell'organizzazione</strong>
              <small>
                Dati dell'organizzazione OpenAI tramite Usage API e Costs API. I costi sono quelli registrati da OpenAI, non una stima locale.
              </small>
            </div>
            <div className="brain-api-usage-links">
              <button type="button" onClick={() => loadApiUsage(true)} disabled={apiUsageRefreshing}>
                {apiUsageRefreshing ? "Aggiorno…" : "Aggiorna dati"}
              </button>
              <a href={apiUsage?.dashboardUrl || "https://platform.openai.com/usage"} target="_blank" rel="noopener noreferrer">
                Usage OpenAI ↗
              </a>
            </div>
          </div>

          <div className="brain-api-usage-grid">
            <div className="money">
              <span>Spesa oggi</span>
              <strong>{fmtMoney(apiUsage?.costs?.today, apiUsage?.costs?.currency)}</strong>
              <small>giorno corrente · UTC</small>
            </div>
            <div className="money primary">
              <span>Spesa mese corrente</span>
              <strong>{fmtMoney(apiUsage?.costs?.monthToDate, apiUsage?.costs?.currency)}</strong>
              <small>month-to-date · dato di fatturazione</small>
            </div>
            <div className="money">
              <span>Mese precedente</span>
              <strong>{fmtMoney(apiUsage?.costs?.previousMonth, apiUsage?.costs?.currency)}</strong>
              <small>mese solare precedente</small>
            </div>
            <div>
              <span>Token GPT / Responses</span>
              <strong>{fmtTokens(apiUsage?.completions?.totalTokens)}</strong>
              <small>{fmtTokens(apiUsage?.completions?.inputTokens)} input · {fmtTokens(apiUsage?.completions?.outputTokens)} output</small>
            </div>
            <div>
              <span>Token embeddings</span>
              <strong>{fmtTokens(apiUsage?.embeddings?.inputTokens)}</strong>
              <small>{fmtTokens(apiUsage?.embeddings?.requests)} richieste embedding</small>
            </div>
            <div>
              <span>Richieste API mese</span>
              <strong>{fmtTokens(apiUsage?.totals?.requests)}</strong>
              <small>{fmtTokens(apiUsage?.completions?.requests)} GPT · {fmtTokens(apiUsage?.embeddings?.requests)} embeddings</small>
            </div>
          </div>

          <div className="brain-api-usage-details">
            <div>
              <strong>Dettaglio token GPT</strong>
              <div className="brain-api-mini-grid">
                <span>Cache <b>{fmtTokens(apiUsage?.completions?.cachedInputTokens)}</b></span>
                <span>Non cache <b>{fmtTokens(apiUsage?.completions?.uncachedInputTokens)}</b></span>
                <span>Cache write <b>{fmtTokens(apiUsage?.completions?.cacheWriteTokens)}</b></span>
              </div>
            </div>
            <div>
              <strong>Credito residuo API</strong>
              <p>Il saldo dei credit grant non è esposto dagli endpoint pubblici Usage/Costs.</p>
              <a href={apiUsage?.balance?.billingUrl || "https://platform.openai.com/settings/organization/billing/credit-grants"} target="_blank" rel="noopener noreferrer">
                Apri credito residuo / Billing ↗
              </a>
            </div>
          </div>

          {apiUsageError && <div className="brain-api-usage-error">{apiUsageError}</div>}

          {(apiUsage?.models || []).length > 0 && (
            <div className="brain-api-usage-table">
              <strong>Modelli usati nel mese</strong>
              {(apiUsage.models || []).slice(0, 8).map((row) => (
                <div className="brain-api-usage-row" key={`${row.kind}-${row.model}`}>
                  <div><b>{row.model}</b><small>{row.kind}</small></div>
                  <span>{fmtTokens(row.totalTokens)} token</span>
                  <span>{fmtTokens(row.requests)} richieste</span>
                </div>
              ))}
            </div>
          )}

          {(apiUsage?.costs?.lineItems || []).length > 0 && (
            <div className="brain-api-usage-table">
              <strong>Costi mese per voce OpenAI</strong>
              {(apiUsage.costs.lineItems || []).slice(0, 8).map((row) => (
                <div className="brain-api-usage-row costs" key={row.lineItem}>
                  <div><b>{row.lineItem}</b></div>
                  <span>{fmtMoney(row.value, apiUsage?.costs?.currency)}</span>
                </div>
              ))}
            </div>
          )}

          <small className="brain-api-usage-foot">
            Ambito: organizzazione OpenAI · periodo token: mese corrente UTC · se nella stessa organizzazione usi altre API oltre al Cervello, sono comprese. I costi possono avere un breve ritardo di contabilizzazione. Codex documentale usa il login ChatGPT ed è separato dal consumo API mostrato qui.
          </small>
        </section>

        <div className="brain-actions">
          {error && <span className="brain-error">{error}</span>}
          <input
            ref={fileInputRef}
            className="brain-file-input"
            type="file"
            accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
            onChange={(e) => uploadDocument(e.target.files?.[0])}
          />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? "Caricamento…" : "📎 Carica documento tecnico"}
          </button>
          <button type="button" onClick={reindex} disabled={reindexing}>{reindexing ? "Aggiornamento…" : "Aggiorna indice"}</button>
        </div>
        <div
          className={`brain-queue ${dragActive ? "dragging" : ""}`}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragActive(false); }}
          onDrop={onDrop}
        >
          <div className="brain-queue-head">
            <div>
              <strong>Documenti tecnici</strong>
              <small>
                Carica → Processa con Codex via ChatGPT → controlla l'anteprima → Inserisci nel Cervello. Nessuna elaborazione automatica.
              </small>
            </div>
            <div className="brain-queue-status">
              <b>{queue?.queuedCount ?? "—"} in coda</b>
              <small>
                {queue?.processor?.ready ? "Codex ChatGPT pronto" : (queue?.processor?.error || "Verifica Codex…")}
              </small>
              <small>Limite upload {queue?.maxUploadMb ?? 80} MB</small>
            </div>
          </div>

          <div className="brain-queue-list">
            {(queue?.items || []).length === 0 && (
              <div className="brain-queue-empty">Nessun documento in attesa.</div>
            )}

            {(queue?.items || []).map((item) => {
              const job = item.job;
              const statusLabel = !job
                ? "In coda"
                : job.status === "processing"
                  ? "Elaborazione…"
                  : job.status === "ready"
                    ? "Pronto da verificare"
                    : job.status === "review"
                      ? "Da correggere"
                      : job.status === "error"
                        ? "Da riprocessare"
                        : job.status;

              return (
                <div className="brain-queue-item" key={item.name}>
                  <div className="brain-queue-file">
                    <strong>{item.name}</strong>
                    <small>{fmtSize(item.size)} · caricato {fmt(item.queuedAt)}</small>
                    {job?.title && <em>{job.title}</em>}
                    {job?.error && <span className="brain-queue-error">{job.error}</span>}
                    {job?.codexUsage && (
                      <span className="brain-queue-usage">
                        {fmtTokens(job.codexUsage.totalTokens)} token · {fmtCredits(job.codexUsage.creditsEquivalent)} crediti eq.
                      </span>
                    )}
                    {job?.warnings?.length > 0 && (
                      <span className="brain-queue-warning">{job.warnings.length} avviso/i da controllare in anteprima</span>
                    )}
                  </div>

                  <div className={`brain-job-state ${job?.status || "queued"}`}>{statusLabel}</div>

                  <div className="brain-queue-buttons">
                    {(!job || job.status === "error") && (
                      <button type="button" onClick={() => processDocument(item, Boolean(job))}>
                        {job ? "Riprocessa" : "Processa"}
                      </button>
                    )}
                    {job?.status === "processing" && <button type="button" disabled>Codex al lavoro…</button>}
                    {["ready", "review"].includes(job?.status) && (
                      <>
                        <button type="button" onClick={() => openCandidate(job.id)} disabled={previewLoading}>
                          {previewLoading ? "Apro…" : (job.status === "review" ? "Correggi candidato" : "Anteprima")}
                        </button>
                        <button type="button" className="secondary" onClick={() => processDocument(item, true)}>
                          Riprocessa
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {(uploadMessage || processMessage) && (
            <div className="brain-upload-message">{processMessage || uploadMessage}</div>
          )}
        </div>
      </header>

      <div className="brain-work">
        <div className="brain-chat">
          {messages.length === 0 && (
            <div className="brain-empty">
              <div className="brain-search">⌕</div>
              <h3>Chiedi qualcosa alla tua wiki</h3>
              <p>Se il dato non è presente nelle note, il Cervello lo segnala invece di inventarlo.</p>
              <div className="brain-examples">
                {examples.map((x) => <button type="button" key={x} onClick={() => ask(x)}>{x}</button>)}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <article key={m.id} className={`brain-msg ${m.role}`}>
              <div className="brain-role">{m.role === "user" ? "Tu" : m.role === "assistant" ? "Cervello" : "Errore"}</div>
              <div className={`brain-text ${m.role === "assistant" ? "brain-text-markdown" : ""}`}>
                {m.role === "assistant" ? (
                  <MarkdownMessage
                    text={m.content}
                    sources={m.sources || []}
                    openSource={openSource}
                  />
                ) : (
                  m.content
                )}
              </div>
              {m.role === "assistant" && m.sources?.length > 0 && (
                <div className="brain-sources">
                  <b>Fonti utilizzate</b>
                  {m.sources.map((s) => (
                    <button type="button" key={`${m.id}-${s.sourceId}-${s.path}`} onClick={() => openSource(s)}>
                      <span>{s.sourceId}</span>
                      <div><strong>{s.title || s.path}</strong><small>{s.path}</small>{s.heading && <em>{s.heading}</em>}</div>
                    </button>
                  ))}
                </div>
              )}
              {m.role === "assistant" && m.model && <small className="brain-model">Modello: {m.model}</small>}
            </article>
          ))}

          {asking && <article className="brain-msg assistant"><div className="brain-role">Cervello</div><div className="brain-dots"><i/><i/><i/></div><small>Cerco e verifico le fonti…</small></article>}
        </div>

        <form className="brain-form" onSubmit={(e) => { e.preventDefault(); ask(); }}>
          <textarea rows={3} maxLength={3000} value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="Es. Come collego…? Cosa dice la wiki su…?"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} />
          <div><small>Invio = chiedi · Shift+Invio = nuova riga</small><button disabled={asking || !question.trim()}>{asking ? "Sto cercando…" : "Chiedi al Cervello"}</button></div>
        </form>
      </div>

      {preview && (
        <div className="brain-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !committingCandidate) setPreview(null); }}>
          <section className="brain-modal brain-candidate-modal">
            <header>
              <div>
                <span>CANDIDATO CODEX · SOURCE-ONLY</span>
                <h3>{preview.job?.title || preview.job?.sourceName}</h3>
                <small>
                  {preview.job?.category || "—"} · destinazione proposta: {preview.job?.suggestedWikiPath || "—"}
                </small>
              </div>
              <button type="button" onClick={() => setPreview(null)} disabled={committingCandidate}>×</button>
            </header>

            <div className="brain-candidate-meta">
              <div><span>Sorgente</span><strong>{preview.job?.sourceName}</strong></div>
              <div><span>Estrattore</span><strong>{preview.job?.extractor || "—"}</strong></div>
              <div><span>Verifica</span><strong>source_only</strong></div>
              <div><span>Categoria</span><strong>{preview.job?.category || "—"}</strong></div>
              <div><span>Tipo</span><strong>{preview.job?.documentType || "—"}</strong></div>
              <div><span>Produttore</span><strong>{preview.job?.manufacturer || "—"}</strong></div>
              <div><span>Modello</span><strong>{preview.job?.model || "—"}</strong></div>
              <div><span>Lingua fonte</span><strong>{preview.job?.sourceLanguage || "—"}</strong></div>
            </div>

            {preview.job?.warnings?.length > 0 && (
              <div className="brain-candidate-warnings">
                <strong>Controlli consigliati</strong>
                {preview.job.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
              </div>
            )}

            <div className="brain-candidate-editor">
              <div className="brain-candidate-split">
                <section className="brain-candidate-preview">
                  <div className="brain-candidate-pane-title">Anteprima della nota</div>
                  <div className="brain-candidate-render">
                    <MarkdownMessage text={markdownBodyOnly(previewText)} sources={[]} openSource={() => {}} />
                  </div>
                </section>
                <section className="brain-candidate-source">
                  <label className="brain-candidate-pane-title" htmlFor="brain-candidate-text">Markdown modificabile</label>
                  <textarea
                    id="brain-candidate-text"
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    spellCheck={false}
                  />
                </section>
              </div>
            </div>

            <footer className="brain-candidate-actions">
              <small>
                Il PDF è ancora in coda. La wiki non viene toccata finché non premi “Inserisci nel Cervello”.
              </small>
              <div>
                <button type="button" className="secondary" onClick={saveCandidate}
                  disabled={savingCandidate || committingCandidate || previewText === previewOriginal}>
                  {savingCandidate ? "Salvo…" : "Salva correzioni"}
                </button>
                <button type="button" className="primary" onClick={commitCandidate}
                  disabled={savingCandidate || committingCandidate || preview?.job?.status !== "ready"}>
                  {committingCandidate ? "Inserimento…" : "Inserisci nel Cervello"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}

      {source && (
        <div className="brain-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setSource(null); }}>
          <section className="brain-modal">
            <header><div><span>FONTE WIKI</span><h3>{source.title || source.path}</h3><small>{source.path}</small></div><button type="button" onClick={() => setSource(null)}>×</button></header>
            <div className="brain-source-body">
              {sourceLoading ? <p>Caricamento fonte…</p> : source.error ? <p className="brain-error">{source.error}</p> : <pre>{source.text}</pre>}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
